import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Avatar from "../components/Avatar";
import { CheckCircle, XCircle, CircleNotch, UserPlus } from "@phosphor-icons/react";

/**
 * Deep link landing for instant friend QR scans:  /app/friend/:token
 * - Looks up the token's owner
 * - One-click accepts the friendship via /api/friend-token/{token}/accept
 */
export default function FriendAccept() {
  const { token } = useParams();
  const { user, fetchMe } = useAuth();
  const nav = useNavigate();
  const [stage, setStage] = useState("lookup"); // lookup | confirm | accepting | done | error
  const [other, setOther] = useState(null);
  const [err, setErr] = useState("");
  const [status, setStatus] = useState(null); // accepted | already_friends

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/friend-token/${token}/lookup`);
        if (data.is_self) {
          setErr("That's your own friend code.");
          setStage("error");
          return;
        }
        setOther(data.user);
        setStage("confirm");
      } catch (e) {
        setErr(formatError(e));
        setStage("error");
      }
    })();
  }, [token]);

  const accept = async () => {
    setStage("accepting");
    try {
      const { data } = await api.post(`/friend-token/${token}/accept`);
      setStatus(data.status);
      setStage("done");
      fetchMe?.();
    } catch (e) {
      setErr(formatError(e));
      setStage("error");
    }
  };

  return (
    <div className="max-w-md mx-auto p-6 space-y-5">
      <div className="text-[10px] uppercase tracking-[0.3em] text-neon-cyan flex items-center gap-2">
        <UserPlus size={12} weight="fill"/> Friend QR
      </div>

      {stage === "lookup" && (
        <div data-testid="friend-accept-loading" className="card-sharp p-6 text-center">
          <CircleNotch size={28} weight="bold" className="text-neon-cyan animate-spin mx-auto"/>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-3">Reading code…</div>
        </div>
      )}

      {stage === "confirm" && other && (
        <div data-testid="friend-accept-confirm" className="card-sharp p-6 text-center">
          <div className="flex flex-col items-center gap-3">
            <Avatar user={other} size={88}/>
            <div>
              <div className="font-display text-2xl">{other.name}</div>
              {other.username && <div className="text-neon-cyan text-sm">@{other.username}</div>}
              {other.location && <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{other.location}</div>}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            You scanned this person&apos;s HigherVision friend code. Connect with them now?
          </p>
          <button
            data-testid="friend-accept-confirm-btn"
            onClick={accept}
            className="btn-neon w-full mt-5"
          >Connect now</button>
          <Link
            to="/app"
            data-testid="friend-accept-cancel"
            className="block text-[10px] uppercase tracking-widest text-muted-foreground mt-3 hover:text-foreground"
          >Cancel</Link>
        </div>
      )}

      {stage === "accepting" && (
        <div className="card-sharp p-6 text-center">
          <CircleNotch size={28} weight="bold" className="text-neon-cyan animate-spin mx-auto"/>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-3">Connecting…</div>
        </div>
      )}

      {stage === "done" && other && (
        <div data-testid="friend-accept-done" className="card-sharp p-6 text-center border-[#F2DC8C]/40" style={{ boxShadow: "0 0 30px rgba(242, 220, 140,0.18)" }}>
          <CheckCircle size={48} weight="fill" className="text-neon-lime mx-auto"/>
          <div className="font-display text-2xl mt-3">
            {status === "already_friends" ? "Already connected" : "You're connected!"}
          </div>
          <div className="text-sm text-muted-foreground mt-2">
            {other.name} is now in your network.
          </div>
          <div className="grid grid-cols-2 gap-2 mt-5">
            <Link to={`/app/u/${other.username || other.user_id}`} className="btn-ghost text-xs" data-testid="friend-accept-view">View profile</Link>
            <Link to="/app/chat" className="btn-neon text-xs" data-testid="friend-accept-friends">Open Chat</Link>
          </div>
        </div>
      )}

      {stage === "error" && (
        <div data-testid="friend-accept-error" className="card-sharp p-6 text-center">
          <XCircle size={36} weight="fill" className="text-neon-pink mx-auto"/>
          <div className="font-display text-lg mt-3">Can&apos;t connect</div>
          <div className="text-sm text-muted-foreground mt-1">{err || "This code didn't work."}</div>
          <button onClick={() => nav("/app")} className="btn-ghost text-xs mt-4">Back to Home</button>
        </div>
      )}
    </div>
  );
}
